/**
 * Send the three lawn plans as a bid they sign.
 *
 * The owner, Sep 20 2026: "send all the info for all 3 plans to the client, they pick one they want
 * and accept like a bid." One tap makes the bid (Full, The Plan, Simple, priced for this yard), then
 * hands back the link and the text. They open it, pick one, type their name, and they are on it.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import CustomerView from './CustomerView';

export default function PlanBidButton({ propertyId, customerId, phone, customerName }: { propertyId: number; customerId: number; phone?: string | null; customerName?: string | null }) {
  const qc = useQueryClient();
  const [made, setMade] = useState<any>(null);
  const [copied, setCopied] = useState<'link' | 'text' | null>(null);
  const [seeing, setSeeing] = useState(false);

  // Preview needs a bid to look at, and making one sends nothing to anybody —
  // it writes the bid and hands back a link. So Preview makes it and opens it
  // in one tap (owner, Sep 21 2026: "can you add a preview button to this
  // page"), and lands in the same place the other button does.
  const [previewOnMake, setPreviewOnMake] = useState(false);
  const make = useMutation(() => api.post('/quotes/lawn-plan', { property_id: propertyId }).then(r => r.data), {
    onSuccess: (d: any) => {
      setMade(d); qc.invalidateQueries('quotes');
      if (previewOnMake) { setSeeing(true); setPreviewOnMake(false); }
      else toast.success(d?.message || 'Plan bid ready');
    },
    onError: (e: any) => {
      const d = e?.response?.data;
      if (d?.token || d?.url) {
        setMade({
          quote_id: d.quote_id || null,
          url: d.url || `${window.location.origin}/q/${d.token}`,
          sms_text: d.sms_text || '',
          plans: d.plans || [],
          already: true,
        });
        toast.info(d.message || 'There is already an open plan bid for this yard');
        return;
      }
      toast.error(d?.message || 'Could not make that bid');
    },
  });
  // quote_id: the text is logged on the bid, so the app knows it delivered it (its follow-ups count from here).
  const textThem = useMutation(() => api.post('/messages/send-sms', {
    to: phone, body: made.sms_text, customer_id: customerId, type: 'plan_bid', quote_id: made.quote_id || null,
  }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Text sent'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that text — copy it and send it from this phone'); },
  });
  const copy = (what: 'link' | 'text', value: string) => {
    navigator.clipboard?.writeText(value);
    setCopied(what); setTimeout(() => setCopied(null), 2000);
  };

  if (!made) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => make.mutate()} disabled={make.isLoading}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
          {make.isLoading && !previewOnMake ? 'Making the bid…' : 'Make the bid they sign'}
        </button>
        <button type="button" onClick={() => { setPreviewOnMake(true); make.mutate(); }} disabled={make.isLoading}
          className="text-xs px-2.5 py-1.5 rounded-lg border border-emerald-300 text-emerald-800 font-semibold disabled:opacity-50">
          {make.isLoading && previewOnMake ? 'Opening…' : 'Preview'}
        </button>
        <span className="text-[11px] text-gray-500">Nothing is sent — you get the link to send yourself.</span>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg border border-emerald-300 bg-white p-2.5 space-y-2">
      {made.already
        ? <p className="text-xs text-gray-800">There is already an open plan bid for this yard. Send it again:</p>
        : <p className="text-xs text-gray-800">Three plans, priced at {Number(made.sqft).toLocaleString()} sq ft of {made.grass}. They pick one and sign.</p>}
      {(made.plans || []).length > 0 && (
        <ul className="text-xs text-gray-800 space-y-0.5">
          {made.plans.map((p: any) => (
            <li key={p.plan}><b>{p.name}</b> — ${p.monthly}/mo · {p.applications} treatments a year{p.recommended ? <span className="text-emerald-700"> · recommended</span> : null}</li>
          ))}
        </ul>
      )}
      {(made.assumed?.sqft || made.assumed?.grass) && (
        <p className="text-[11px] text-amber-700">
          {made.assumed.sqft ? 'The lawn is not measured, so it is priced on an assumption. ' : ''}{made.assumed.grass ? 'The grass is not on file, so it is priced as the dearer one. ' : ''}Measure it and the price follows.
        </p>
      )}
      <code className="block text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1 truncate">{made.url}</code>
      {seeing && <CustomerView url={made.url} title={`What ${customerName || 'they'} see`} onClose={() => setSeeing(false)} />}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setSeeing(true)} className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-800 font-semibold">Preview</button>
        <button type="button" onClick={() => copy('link', made.url)} className="text-xs px-2 py-1 rounded bg-emerald-700 text-white font-semibold">{copied === 'link' ? 'Copied' : 'Copy link'}</button>
        {made.sms_text && <button type="button" onClick={() => copy('text', made.sms_text)} className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-800 font-semibold">{copied === 'text' ? 'Copied' : 'Copy the text to send'}</button>}
        {phone && made.sms_text && (
          <button type="button" onClick={() => textThem.mutate()} disabled={textThem.isLoading}
            className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-800 font-semibold disabled:opacity-50">
            {textThem.isLoading ? 'Sending…' : 'Text them now'}
          </button>
        )}
        {phone && made.sms_text && <a href={`sms:${phone}?&body=${encodeURIComponent(made.sms_text)}`} className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-800 font-semibold">Text it from this phone</a>}
      </div>
      <p className="text-[11px] text-gray-500">They pick Estate, The Plan or Simple, sign, and the plan starts. Nothing is charged by signing.</p>
    </div>
  );
}
